import { Component, Input } from '@angular/core';
import {
    EntityBaseRootFormComponent,
    PlatformFormsModule,
} from '@pf/shared-ui';
import { IEntity, IManageEntityFacade, SafeAny } from '@pf/shared-common';
import {
    AssociatedAddressDto,
    IAssociatedAddressDto,
} from '@pf/shared/util-platform';
import { Controls } from 'ngx-sub-form';
import { AssociatedAddressType } from '@control-tower/platform-core';
import { FormControl, Validators } from '@angular/forms';
import { asArraySafe } from '@pf/shared-utility';
import { SharedUiPlatformModule } from '../shared-ui-platform.module';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { switchMap, tap } from 'rxjs';

export interface EntityWithAssociatedAddress extends IEntity {
    id: string;
    associatedAddresses?: AssociatedAddressDto[];
}

interface EntityAddressForm {
    address?: IAssociatedAddressDto | null;
}

@UntilDestroy()
@Component({
    selector: 'platform-entity-address-form',
    template: `
        <form [formGroup]="form.formGroup" nz-form>
            <platform-address-form
                [rootEntityType]="entityType"
                [associatedAddressType]="associatedAddressType"
                [associatedAddress]="true"
                [embeddedForm]="true"
                [formControlName]="form.formControlNames.address">
            </platform-address-form>
        </form>
    `,
    imports: [PlatformFormsModule, SharedUiPlatformModule],
    styles: [``],
})
export class EntityAssociatedAddressFormComponent extends EntityBaseRootFormComponent<
    EntityWithAssociatedAddress,
    EntityAddressForm
> {
    @Input({ required: true }) entityType!: string;
    @Input({ required: true }) associatedAddressType!: AssociatedAddressType;
    @Input({ required: true }) entityFacade!: IManageEntityFacade<SafeAny>;

    constructor() {
        super();

        this.listenForSubmit();
    }

    formControls(): Controls<EntityAddressForm> {
        return {
            address: new FormControl<IAssociatedAddressDto | null>(null, [
                Validators.required,
            ]),
        };
    }
    toFormGroup(entity: EntityWithAssociatedAddress): EntityAddressForm {
        const associatedAddress = asArraySafe(entity.associatedAddresses).find(
            aa => aa.associatedAddressType === this.associatedAddressType
        );

        if (!associatedAddress) {
            return {
                address: {} as AssociatedAddressDto,
            };
        }

        return {
            address: associatedAddress as AssociatedAddressDto,
        };
    }
    fromFormGroup(formValue: EntityAddressForm): EntityWithAssociatedAddress {
        if (!this.initialValue) {
            return {} as EntityWithAssociatedAddress;
        }
        const associatedAddresses =
            this.initialValue?.associatedAddresses || [];
        const associatedAddressIndex = associatedAddresses.findIndex(
            aa => aa.associatedAddressType === this.associatedAddressType
        );
        if (associatedAddressIndex > -1) {
            associatedAddresses[associatedAddressIndex] =
                formValue.address as AssociatedAddressDto;
        } else {
            associatedAddresses.push(formValue.address as AssociatedAddressDto);
        }
        return {
            ...this.initialValue,
            associatedAddresses,
        };
    }

    private listenForSubmit() {
        this.output$
            .pipe(
                untilDestroyed(this),
                switchMap(() => this.entityFacade.save$(this.initialValue)),
                tap(entity =>
                    this.formService.submitted(this.entityType, entity)
                )
            )
            .subscribe();
    }
}
